/* Keeps the RetailOS workspace object in step with ezdocs' database.

   The app holds one `workspace` object and edits it through mutate(). Rather
   than teach every edit site about the API, this diffs the object against the
   last synced copy and posts only the records that changed, with the versions
   it last saw for them. The server applies a batch all-or-nothing: a stale
   version comes back as 409 and the app reloads. Team kinds are shared by
   everyone with the interface; views and the watchlist are private. */
import { API_ENDPOINTS } from '../../config/api';

const headers = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('jwtToken'),
  'Content-Type': 'application/json',
});

// Thrown when the server applied nothing: a stale version (409) or a delete of
// someone else's record by a non-administrator (403). The app reloads either way.
export class SyncConflict extends Error {
  constructor(conflicts, message) { super(message || 'Someone else changed this workspace first — reloaded with their version.'); this.conflicts = conflicts || []; }
}

export async function fetchWorkspace() {
  const r = await fetch(API_ENDPOINTS.RETAILOS_WORKSPACE, { headers: headers() });
  if (!r.ok) throw new Error(`Workspace request failed (${r.status})`);
  return r.json();
}

export async function pushChanges(changes, events) {
  const r = await fetch(API_ENDPOINTS.RETAILOS_WORKSPACE_CHANGES, {
    method: 'POST', headers: headers(), body: JSON.stringify({ changes, events }),
  });
  if (r.status === 409 || r.status === 403) {
    let body = {}; try { body = await r.json(); } catch (e) { /* no body */ }
    throw new SyncConflict(body.conflicts || body.forbidden, r.status === 403 ? 'Only an Administrator can remove records created by someone else — reloaded the team’s version.' : undefined);
  }
  if (!r.ok) throw new Error(`Workspace save failed (${r.status})`);
  return r.json();
}

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const vkey = (kind, key) => `${kind}:${key}`;

function byId(list, idKey) {
  const m = new Map();
  for (const x of list || []) if (x && x[idKey] != null) m.set(String(x[idKey]), x);
  return m;
}

function diffList(kind, prev, next, idKey, versions, out) {
  const p = byId(prev, idKey), n = byId(next, idKey);
  for (const [k, v] of n) if (!p.has(k) || !same(p.get(k), v))
    out.push({ kind, key: k, op: 'upsert', payload: v, version: versions[vkey(kind, k)] ?? null });
  for (const k of p.keys()) if (!n.has(k))
    out.push({ kind, key: k, op: 'delete', version: versions[vkey(kind, k)] ?? null });
}

// Keyed objects. `wrap` turns a scalar value (a target amount) into a payload.
function diffMap(kind, prev, next, versions, out, wrap = v => v) {
  const p = prev || {}, n = next || {};
  for (const k of Object.keys(n)) if (!(k in p) || !same(p[k], n[k]))
    out.push({ kind, key: k, op: 'upsert', payload: wrap(n[k]), version: versions[vkey(kind, k)] ?? null });
  for (const k of Object.keys(p)) if (!(k in n))
    out.push({ kind, key: k, op: 'delete', version: versions[vkey(kind, k)] ?? null });
}

/** Records that differ between two workspace objects, as API changes. */
export function diffWorkspace(prev, next, versions = {}) {
  const out = [];
  diffList('task', prev.tasks, next.tasks, 'id', versions, out);
  diffList('draft', prev.promos, next.promos, 'id', versions, out);
  diffMap('review', prev.promotionReviews, next.promotionReviews, versions, out);
  diffMap('note', prev.accountNotes, next.accountNotes, versions, out);
  diffMap('target', prev.targets, next.targets, versions, out, v => ({ value: v }));
  diffMap('mtarget', prev.monthlyTargets, next.monthlyTargets, versions, out, v => ({ value: v }));
  if (!same(prev.settings, next.settings))
    out.push({ kind: 'settings', key: 'team', op: 'upsert', payload: next.settings || {}, version: versions['settings:team'] ?? null });
  if (!same([prev.ads, prev.adsImportedAt, prev.adSourceName], [next.ads, next.adsImportedAt, next.adSourceName]))
    out.push({ kind: 'ads', key: 'team', op: 'upsert',
      payload: { rows: next.ads || [], importedAt: next.adsImportedAt || null, source: next.adSourceName || null },
      version: versions['ads:team'] ?? null });
  diffList('view', prev.views, next.views, 'id', versions, out);
  if (!same(prev.watchlist, next.watchlist))
    out.push({ kind: 'watchlist', key: 'me', op: 'upsert', payload: { keys: next.watchlist || [] }, version: versions['watchlist:me'] ?? null });
  return out;
}

/* Edits made within the debounce window are merged by record: the latest op
   and payload win, but the version is the one from the FIRST edit, because
   that is the version the server still holds until this batch lands. */
export function mergePending(pending, changes) {
  const m = new Map(pending.map(c => [vkey(c.kind, c.key), c]));
  for (const c of changes) {
    const k = vkey(c.kind, c.key), prior = m.get(k);
    m.set(k, prior ? { ...c, version: prior.version } : c);
  }
  return [...m.values()];
}
